/**
 * API Service - Wrapper around API Client
 * 
 * This service provides a unified interface for data fetching,
 * allowing the app to switch between local JSON and API backend.
 * 
 * Created by: Zee
 */

import api from '../api/client';

// Always use API - no fallback to local data
// Import local services only for reference (will be removed)

/**
 * Events Service - Uses Unified Backend API
 */
export const eventsService = {
  async getAllEvents() {
    try {
      const response = await api.events.getAll();
      return response.data || [];
    } catch (error) {
      console.error('Error fetching events:', error);
      return []; // Return empty array on error
    }
  },

  async getEventById(id) {
    try {
      const response = await api.events.getById(id);
      return response.data;
    } catch (error) {
      console.error('Error fetching event:', error);
      return null;
    }
  },

  async getEventBySlug(slug) {
    try {
      const response = await api.events.getBySlug(slug);
      return response.data;
    } catch (error) {
      console.error('Error fetching event by slug:', error);
      return null;
    }
  },

  async getEventByCode(code) {
    try {
      const response = await api.events.getByCode(code);
      return response.data;
    } catch (error) {
      console.error('Error fetching event by code:', error);
      return null;
    }
  },

  async getUpcomingEvents() {
    try {
      const allEvents = await this.getAllEvents();
      const now = new Date();
      return allEvents
        .filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= now && event.isActive;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      return [];
    }
  },

  async getPastEvents() {
    try {
      const allEvents = await this.getAllEvents();
      const now = new Date();
      return allEvents
        .filter(event => {
          const eventDate = new Date(event.date);
          return eventDate < now || !event.isActive;
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    } catch (error) {
      console.error('Error fetching past events:', error);
      return [];
    }
  },

  async getNextEvent() {
    try {
      const upcoming = await this.getUpcomingEvents();
      return upcoming.length > 0 ? upcoming[0] : null;
    } catch (error) {
      console.error('Error fetching next event:', error);
      return null;
    }
  }
};

/**
 * Artists Service - Uses Unified Backend API
 * Note: Artists not yet implemented in unified backend, return empty for now
 */
export const artistsService = {
  async getAllArtists() {
    // TODO: Implement artists API in unified backend
    // const response = await api.artists.getAll();
    // return response.data || [];
    return []; // Return empty until artists API is implemented
  },

  async getArtistById(id) {
    // TODO: Implement artists API in unified backend
    return null;
  },

  getArtistsByIds(ids) {
    // Helper for EventCard - return empty for now
    return [];
  },

  getFeaturedArtists() {
    return [];
  }
};

/**
 * Content Service - Uses Unified Backend API
 */
export const contentService = {
  async getAllGalleryItems() {
    try {
      // Get all events and extract photos
      const events = await eventsService.getAllEvents();
      const galleryItems = [];
      
      events.forEach(event => {
        if (event.gallery && Array.isArray(event.gallery)) {
          event.gallery.forEach(photo => {
            galleryItems.push({
              id: photo.id,
              eventId: event.id,
              type: photo.mimeType?.startsWith('image/') ? 'image' : 'video',
              url: photo.url,
              thumbnail: photo.thumbnailUrl,
              caption: photo.filename,
              date: photo.uploadedAt || event.date,
              tags: []
            });
          });
        }
      });
      
      return galleryItems;
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      return [];
    }
  },

  async getGalleryByEvent(eventId) {
    try {
      const event = await eventsService.getEventById(eventId);
      if (event && event.gallery) {
        return event.gallery.map(photo => ({
          id: photo.id,
          eventId: eventId,
          type: photo.mimeType?.startsWith('image/') ? 'image' : 'video',
          url: photo.url,
          thumbnail: photo.thumbnailUrl,
          caption: photo.filename,
          date: photo.uploadedAt || event.date,
          tags: []
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching gallery by event:', error);
      return [];
    }
  },

  async getAllFlyers() {
    try {
      // Get all events and extract cover images as flyers
      const events = await eventsService.getAllEvents();
      return events
        .filter(event => event.coverImage)
        .map(event => ({
          id: `flyer-${event.id}`,
          eventId: event.id,
          title: event.title,
          url: event.coverImage,
          date: event.date
        }));
    } catch (error) {
      console.error('Error fetching flyers:', error);
      return [];
    }
  },

  getAllRecordings() {
    // TODO: Implement recordings API in unified backend
    return [];
  },

  getRecordingById(id) {
    // TODO: Implement recordings API in unified backend
    return null;
  }
};

/**
 * RSVP Service (API only)
 */
export const rsvpService = {
  async createRSVP(rsvpData) {
    try {
      const response = await api.rsvp.create(rsvpData);
      return response;
    } catch (error) {
      console.error('Error creating RSVP:', error);
      throw error;
    }
  },

  async getRSVPsByEvent(eventId) {
    try {
      const response = await api.rsvp.getByEvent(eventId);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching RSVPs:', error);
      return [];
    }
  },

  async calculateTravelTime(fromLocation, toLocation, eventDate) {
    try {
      const response = await api.rsvp.calculateTravelTime(fromLocation, toLocation, eventDate);
      return response.data;
    } catch (error) {
      console.error('Error calculating travel time:', error);
      throw error;
    }
  },

  async scheduleLocationReveal(rsvpId, revealData) {
    try {
      const response = await api.rsvp.scheduleLocationReveal(rsvpId, revealData);
      return response;
    } catch (error) {
      console.error('Error scheduling location reveal:', error);
      throw error;
    }
  },

  async getLocationRevealStatus(eventId) {
    try {
      const response = await api.rsvp.getLocationRevealStatus(eventId);
      return response.data;
    } catch (error) {
      console.error('Error fetching location reveal status:', error);
      return null;
    }
  },

  async triggerLocationReveal(eventId, options = {}) {
    try {
      const response = await api.rsvp.triggerLocationReveal(eventId, options);
      return response;
    } catch (error) {
      console.error('Error triggering location reveal:', error);
      throw error;
    }
  }
};

/**
 * Subscription Service (API only)
 */
export const subscriptionService = {
  async subscribe(name, email, interests = []) {
    try {
      const response = await api.users.subscribe({ name, email, interests });
      return response;
    } catch (error) {
      console.error('Error subscribing:', error);
      throw error;
    }
  },

  async unsubscribe(email) {
    try {
      const response = await api.users.unsubscribe(email);
      return response;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    }
  }
};

/**
 * Contact Service (API only)
 */
export const contactService = {
  async sendContactMessage(messageData) {
    try {
      const response = await api.contact.sendMessage(messageData);
      return response;
    } catch (error) {
      console.error('Error sending contact message:', error);
      throw error;
    }
  }
};

/**
 * Messages Service (API only)
 */
export const messagesService = {
  async getMyMessages() {
    try {
      const response = await api.messages.getAll();
      return response.data || [];
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  },

  async sendMessage(messageData) {
    try {
      const response = await api.messages.send(messageData);
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  async getMessageById(id) {
    try {
      const response = await api.messages.getById(id);
      return response.data;
    } catch (error) {
      console.error('Error fetching message:', error);
      return null;
    }
  },

  async markAsRead(id) {
    try {
      const response = await api.messages.markAsRead(id);
      return response;
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  },

  async getConversations() {
    try {
      const response = await api.messages.getConversations();
      return response.data || [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  }
};

/**
 * Tickets Service (API only)
 */
export const ticketsService = {
  async createTicket(ticketData) {
    try {
      const response = await api.tickets.create(ticketData);
      return response;
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  },

  async getMyTickets(customerId) {
    try {
      const response = await api.tickets.getMy(customerId);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching tickets:', error);
      return [];
    }
  },

  async getTicketById(id, customerId) {
    try {
      const response = await api.tickets.getById(id, customerId);
      return response.data;
    } catch (error) {
      console.error('Error fetching ticket:', error);
      return null;
    }
  },

  async getTicketQRCode(ticketId, customerId) {
    try {
      const response = await api.tickets.getQRCode(ticketId, customerId);
      return response.data;
    } catch (error) {
      console.error('Error fetching ticket QR code:', error);
      return null;
    }
  },

  async validateTicket(scanData) {
    try {
      const response = await api.tickets.validate(scanData);
      return response;
    } catch (error) {
      console.error('Error validating ticket:', error);
      throw error;
    }
  }
};

/**
 * Products Service (API only)
 */
export const productsService = {
  async getAllProducts(filters = {}) {
    try {
      const response = await api.products.getAll(filters);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  },

  async getProductById(id) {
    try {
      const response = await api.products.getById(id);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  },

  async getProductsByCategory(category) {
    try {
      const response = await api.products.getByCategory(category);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return [];
    }
  },

  async searchProducts(query) {
    try {
      const response = await api.products.getAll({ search: query });
      return response.data || [];
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }
};

/**
 * Orders Service (API only)
 */
export const ordersService = {
  async createOrder(orderData) {
    try {
      const response = await api.orders.create(orderData);
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  async getMyOrders() {
    try {
      const response = await api.orders.getMy();
      return response.data || [];
    } catch (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
  },

  async getOrderById(id) {
    try {
      const response = await api.orders.getById(id);
      return response.data;
    } catch (error) {
      console.error('Error fetching order:', error);
      return null;
    }
  }
};

// Export all services
const services = {
  events: eventsService,
  artists: artistsService,
  content: contentService,
  rsvp: rsvpService,
  subscription: subscriptionService,
  contact: contactService,
  messages: messagesService,
  tickets: ticketsService,
  products: productsService,
  orders: ordersService
};

export default services;


